import type { TFunction } from 'i18next';
import type { Params } from 'react-router';

import type { PageHeading } from '@/components/layout/AppShell';
import { brandTabPath } from '@/config/paths';
import type { LiveHeading, RouteHandle } from '@/config/routes';
import type { Membership, Session } from '@/features/auth/session';
import { isBrandTab } from '@/features/brandHub/brand/brand';
import type { Brand } from '@/features/brandHub/brand/types';
import { BRAND_CREATE_STEPS, readBrandCreateStep } from '@/features/brandHub/brandCreate/brandCreateSteps';
import type { Template } from '@/features/brandHub/templates/types';
import type { Campaign } from '@/features/campaigns/campaign/types';
import { formatDate } from '@/lib/format';

/** The live data a heading can show. */
export interface HeadingContext {
  t: TFunction;
  language: string;
  today: Date;
  params: Readonly<Params>;
  searchParams: URLSearchParams;
  campaign: Campaign | null;
  campaigns: Campaign[];
  brands: Brand[];
  templates: Template[];
  session: Session | null;
  membership: Membership | null;
}

const DASHBOARD_DATE: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' };

/** Returns true when a route `handle` has its route table row. */
export function isRouteHandle(handle: unknown): handle is RouteHandle {
  return typeof handle === 'object' && handle !== null && 'route' in handle;
}

/** Returns the brand name as the overview title, or the tab name with the brand name below it. */
function brandHeading({ t, params, brands }: HeadingContext): Partial<PageHeading> {
  const { brandSlug, tab } = params;
  const brand = brands.find((one) => one.id === brandSlug);
  const isOverview = !isBrandTab(tab) || tab === 'overview';
  const state = brand ? t(`brandHub.brand.state.${brand.state}`) : '';
  return {
    title: isOverview ? (brand?.name ?? t('brandHub.brand.title')) : t(`brandHub.brand.tabs.${tab}`),
    subtitle: brand && (isOverview ? state : t('brandHub.brand.nameState', { name: brand.name, state })),
  };
}

/** Returns the step count and the step name of the brand wizard. */
function brandCreateHeading({ t, searchParams }: HeadingContext): Partial<PageHeading> {
  const step = readBrandCreateStep(searchParams);
  return {
    subtitle: t('brandHub.brandCreate.progress', {
      step: BRAND_CREATE_STEPS.indexOf(step) + 1,
      total: BRAND_CREATE_STEPS.length,
      name: t(`brandHub.brandCreate.step.${step}`),
    }),
  };
}

/** Returns the brand name below the title, and a back link to the products of the brand. */
function productBrandHeading({ t, params, brands }: HeadingContext): Partial<PageHeading> {
  const brand = brands.find((one) => one.id === params.brandSlug);
  return {
    subtitle: brand?.name,
    crumb: { label: brand?.name ?? t('nav.brands'), to: brandTabPath(params.brandSlug ?? '', 'products') },
  };
}

/** Returns the template name as the title. */
function templateHeading({ t, params, templates }: HeadingContext): Partial<PageHeading> {
  const template = templates.find((one) => one.id === params.templateId);
  return {
    title: template?.name ?? t('brandHub.template.editTitle'),
    subtitle: template && t('brandHub.template.editTitle'),
  };
}

/** Returns, for each live heading, the fields that replace the static heading of the row. */
export const LIVE_HEADING: Record<LiveHeading, (context: HeadingContext) => Partial<PageHeading>> = {
  dashboardDate: ({ t, language, today }) => ({
    subtitle: t('dashboard.subtitle', { date: formatDate(today, language, DASHBOARD_DATE) }),
  }),
  campaignCount: ({ t, campaigns }) => ({
    subtitle: t('campaigns.countSummary', {
      count: campaigns.length,
      active: campaigns.filter((one) => one.status === 'complete').length,
    }),
  }),
  campaignName: ({ campaign }) => ({ subtitle: campaign?.name }),
  brandCreateStep: brandCreateHeading,
  brand: brandHeading,
  productBrand: productBrandHeading,
  templateName: templateHeading,
  sessionUser: ({ t, session, membership }) => ({
    subtitle: session
      ? t('settings.userRole', { name: session.name, role: t(`settings.role.${membership?.role ?? 'member'}`) })
      : undefined,
  }),
};
